/**
 * lib/data/upsell-strings.ts — EVERY visible string of the after-purchase
 * offers and the e-book consent that pianofantasy-web renders, in one place, so
 * the copy is a single swap (lane sf-pro-upsell, 2026-10-02).
 *
 * Source of the words: "Team Inbox/sf-pro-upsell/copy-v2-cleared.md" (Hazel,
 * audited by Vera). Keys follow that file:
 *   B1–B3  Offer B on the membership checkout's success screen
 *   C1     the "download now, no withdrawal" consent tick-box (every surface
 *          that sells a download: CheckoutModal one-time mode — Super Fingers
 *          and Distance — and the Offer B card)
 *   C2     the "we keep your card for a few minutes" line (Super Fingers only)
 * Offer A and the email confirmation sentence are rendered by pf-platform and
 * live in ITS strings module (lib/commerce/upsell/strings.ts).
 *
 * Copy: copy-v2-cleared.md ("copy v3"), CLEARED by Vera 2026-10-02 round 3,
 * plus its "Added 2026-10-02 (build gaps)" keys.
 *
 * Placeholders are filled from the server's numbers, never typed:
 *   {book_member_price}  product_prices members' price, formatted by pf-platform
 *   {book_price}         product_prices public price, formatted by pf-platform
 * A price is NEVER written in this file (SOP-OPS-18; PRICING-CANONICAL.md).
 *
 * Banned on every user-facing state: "expired", "0:00", "missed".
 */

export const UPSELL_STRINGS = {
  /**
   * upsell-pages-v2 (2026-10-02): Offer B's words are now LARS'S OWN PLAN,
   * verbatim — Team Inbox/sf-pro-upsell/upsell-pages-build-instruction.md §3.1.
   * The v1 keys he replaced (B1 "Get the book too.", the B2 paragraph and the
   * "Pay … now" button) are gone, not hidden.
   */
  /** Compact confirmation line at the top of the card — "You're in" bold, then the plan name. */
  'B0-in': 'You\u2019re in',
  /** The book's title, under the book image. */
  'B0-title': 'Super Fingers: Essential Piano Technique',
  /** Offer B headline. */
  B1: 'Take the exercises to the piano.',
  /** Offer B deck. */
  B2: 'All 60 exercises and my written instructions in one book \u2014 print it, mark it up at the piano, and keep it, even if you ever leave Pro.',
  /** Bullets: lead (600) + rest, exactly as the plan writes them. */
  'B2-b1-lead': 'Print it',
  'B2-b1-rest': ' and mark it up at the piano',
  'B2-b2-lead': 'Every exercise',
  'B2-b2-rest': ' in one place',
  'B2-b3-lead': 'Yours to keep',
  'B2-b3-rest': ' \u2014 even if you ever leave Pro',
  /** After the struck {book_price} and the bold {book_member_price}. */
  'B2-price-tail': 'members\u2019 price',
  /** Yes button — names the amount ({book_member_price}). */
  B3: 'Add the book \u2014 {book_member_price}',
  'B3-processing': 'Adding your book\u2026',
  /**
   * Variant 2 ("without another card"): Offer B charges the card the membership
   * was just paid with and has no way to enter a different one (Vera's condition).
   */
  'B3-error': 'Your card was declined. Try again.',
  /** "No thanks, take me to my account". */
  'B3-decline': 'No thanks, take me to my account',
  /** The timer line, directly under the button (plan §1.4) — uses {minutes_left}. */
  'B4-timer': 'This offer ends in {minutes_left}',
  /**
   * The testimonial (plan §1.6) — the same one on both offers. VERBATIM from
   * pf-platform's testimonials table (production row
   * 38434219-18ce-4736-a513-3e740ed25cfb, read 2026-10-02): the source says
   * "PDF's", the plan says "PDFs" — Eleanor's ruling (round 2): the SOURCE text,
   * verbatim, never corrected. "Pro Member": production read-only shows an
   * active Pro (tier premium) subscription for his user (2026-10-02).
   * Byte-identical to pf-platform lib/commerce/upsell/strings.ts T-quote.
   */
  'T-quote':
    "Even after a short time working on Modules 1 & 2, found a marked improvement in surety of finger control. Combining Lars demonstrations and the PDF's are working well for me.",
  'T-name': 'Edward Lopata',
  'T-role': 'Pro Member',
  /** Alt text for the 3D books image (Hazel, copy-gaps-v1 §5). */
  'B0-alt-books': 'Super Fingers: Essential Piano Technique, shown as a 3D book mockup.',
  /** The required consent tick-box (unticked by default). */
  'C1-checkbox':
    'I want this download right away, and I understand that means giving up my 14-day right to cancel and get a refund on it.',
  /** Inline message when Pay is pressed (or the server refuses) without the consent. */
  'C1-required': 'Tick the box above to continue.',
  /** Helper under the tick-box until it is ticked (plan §3.1) — 16px, muted. */
  'C1-helper': 'Tick the box to continue.',
  /** "We keep your card for a few minutes" — Super Fingers book checkout only. */
  C2: 'We\u2019ll save your card details for up to 15 minutes in case you want the next offer \u2014 one tap, nothing typed again. We forget them if you decline; if you say yes, they become your Pro card.',
} as const;

export type UpsellStringKey = keyof typeof UPSELL_STRINGS;

type UpsellPlaceholder = 'book_member_price' | 'book_price' | 'minutes_left';

export function fillUpsellString(key: UpsellStringKey, values: Partial<Record<UpsellPlaceholder, string>> = {}): string {
  return UPSELL_STRINGS[key].replace(/\{([a-z_]+)\}/g, (whole, name: string) => {
    const v = values[name as UpsellPlaceholder];
    return typeof v === 'string' ? v : whole;
  });
}

/** "m:ss" for the timer line. Never renders 0:00 — at zero the offer is replaced. */
export function formatMinutesLeft(secondsLeft: number): string {
  const s = Math.max(1, Math.ceil(secondsLeft));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** The server's refusal code for a download payment without the consent. */
export const WITHDRAWAL_WAIVER_REQUIRED = 'withdrawal_waiver_required';
